import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { doc, updateDoc, collection, getDocs, deleteField } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import Modal from '../../../../components/Modal';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import SearchableSelect from '../../../../components/SearchableSelect';
import Icon from '../../../../components/Icon';
import PakistanPlaceSearch from '../../../../components/PakistanPlaceSearch';
import BranchMapPickerModal from '../../../../components/BranchMapPickerModal';
import BranchRoutePreviewMap from '../../../../components/BranchRoutePreviewMap';
import { FiEdit2, FiMapPin, FiPhone, FiHash, FiLock, FiLoader, FiPlus, FiChevronDown, FiX, FiLayers } from 'react-icons/fi';
import { fetchNearestPoliceStation } from '../../../../utils/fetchNearestPoliceStation';
import { fetchNearestAreaLabel, fetchNearbyBankNamesCsv } from '../../../../utils/fetchNearestLocationAndBanks';
import {
    BRANCH_TYPE_OPTIONS,
    COVERED_AREA_OPTIONS,
    SRA_LEVEL_OPTIONS,
    parseGeoForSubmit,
    type BranchFormData,
} from '../AddBranchModal/service/addBranchService';
import { haversineDistanceKm, formatDistanceKm } from '../../../../utils/haversineDistanceKm';
import { fetchDrivingDistanceMeters, formatDrivingKmLabel } from '../../../../utils/fetchDrivingDistanceMeters';
import { useAppSelector } from '../../../../store';
import { designationIsMisIncharge } from '../../../../utils/designationRole';
type LocationOption = {
    id: string;
    name: string;
    location?: { lat?: number | null; long?: number | null } | null;
};

type Branch = {
    id: string;
    bankId: string;
    branchCode: string;
    address: string;
    mobile: string;
    locationId: string;
    securityCode: string;
    locationName: string;
    psoId?: string;
    psoName: string;
    psoEmail: string;
    psoContact: string;
    name?: string;
    branchManagerId?: string;
    operationalManagerId?: string;
    areaManagerId?: string;
    branchType?: string;
    branchTypeOther?: string;
    cashLimitMillion?: number;
    atmAvailable?: boolean;
    atmMountType?: 'wall_mounted' | 'standalone' | null;
    atmSeparateFromMainEntrance?: boolean | null;
    vantagePointAvailable?: boolean;
    emergencyExitAvailable?: boolean;
    lockerAvailable?: boolean;
    coveredArea?: string;
    landmark?: string;
    nearestOtherBanksBranches?: string;
    nearestPoliceStationName?: string;
    nearestPoliceStationAddress?: string;
    nearestPoliceStationPhone?: string;
    location?: { lat?: number | null; long?: number | null; distanceFromRegion?: string };
    latitudeLongitude?: string;
    distanceFromRegion?: string;
    sraLevel?: string;
    sraReason?: string;
    eventAffectedActive?: boolean;
    eventAffectedAreasNote?: string;
    saturdayOpen?: boolean;
    numberOfGuardsDay?: number;
    numberOfGuardsNight?: number;
    departments?: string[];
    cdmAvailable?: boolean;
    accessControlAvailable?: boolean;
    accessControlUnits?: number | string;
    accessControlLocations?: string[];
};

type Manager = {
    id: string;
    name: string;
    email: string;
    mobile: string;
    type?: string;
};

type PSO = {
    id: string;
    name: string;
    email?: string;
    contact?: string;
};

type BranchDetailsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    branch: Branch | null;
    managers: Record<string, Manager>;
    bankId?: string;
    onUpdate?: () => void;
    editMode?: boolean;
};

function YesNoSwitch({
    label,
    value,
    onChange,
    disabled,
}: {
    label: string;
    value: boolean;
    onChange: (v: boolean) => void;
    disabled?: boolean;
}) {
    return (
        <div className="flex min-w-0 w-full flex-col gap-1.5">
            <span className="form-field-label leading-tight">{label}</span>
            <div
                role="group"
                aria-label={label}
                className={`grid h-9 w-full grid-cols-2 overflow-hidden rounded-lg border border-gray-300 bg-gray-100 shadow-sm dark:border-gray-600 dark:bg-gray-900/40 ${
                    disabled ? 'pointer-events-none opacity-50' : ''
                }`}
            >
                <button
                    type="button"
                    onClick={() => onChange(false)}
                    className={`text-xs font-semibold tracking-wide transition ${
                        !value
                            ? 'bg-gray-600 text-white dark:bg-gray-600'
                            : 'bg-transparent text-gray-500 hover:bg-gray-200/80 dark:text-gray-400 dark:hover:bg-gray-800/80'
                    }`}
                >
                    No
                </button>
                <button
                    type="button"
                    onClick={() => onChange(true)}
                    className={`text-xs font-semibold tracking-wide transition ${
                        value
                            ? 'bg-emerald-600 text-white dark:bg-emerald-600'
                            : 'bg-transparent text-gray-500 hover:bg-gray-200/80 dark:text-gray-400 dark:hover:bg-gray-800/80'
                    }`}
                >
                    Yes
                </button>
            </div>
        </div>
    );
}

function FormRow({ children, cols = 4 }: { children: React.ReactNode; cols?: 2 | 3 | 4 | 5 }) {
    const grid =
        cols === 2
            ? 'sm:grid-cols-2'
            : cols === 3
              ? 'sm:grid-cols-2 lg:grid-cols-3'
              : cols === 5
                ? 'sm:grid-cols-2 lg:grid-cols-5'
                : 'sm:grid-cols-2 lg:grid-cols-4';
    return <div className={`grid w-full grid-cols-1 gap-3 ${grid}`}>{children}</div>;
}

function FormCell({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
    return <div className={`min-w-0 ${className}`}>{children}</div>;
}

function normStr(v: unknown): string {
    return String(v ?? '').trim();
}

function normMgrId(v: unknown): string {
    return normStr(v);
}

function cashToNumberOrNull(v: unknown): number | null {
    const t = String(v ?? '').replace(/,/g, '').trim();
    if (t === '') return null;
    const n = parseFloat(t);
    return Number.isNaN(n) ? null : n;
}

function nonNegativeIntOrNull(v: unknown): number | null {
    const t = String(v ?? '').trim();
    if (t === '') return null;
    const n = parseInt(t, 10);
    if (!Number.isFinite(n) || n < 0) return null;
    return n;
}

/** True if lat/lng inputs differ; ignores distance text so auto driving-distance refresh does not force a write. */
function branchCoordsDiffer(e: { latitude?: unknown; longitude?: unknown }, b: { latitude?: unknown; longitude?: unknown }) {
    const p = (s: unknown) => parseFloat(normStr(s).replace(/,/g, ''));
    const elat = p(e.latitude);
    const elong = p(e.longitude);
    const blat = p(b.latitude);
    const blng = p(b.longitude);
    const eValid = !Number.isNaN(elat) && !Number.isNaN(elong);
    const bValid = !Number.isNaN(blat) && !Number.isNaN(blng);
    if (eValid && bValid) {
        return Math.abs(elat - blat) > 1e-8 || Math.abs(elong - blng) > 1e-8;
    }
    return normStr(e.latitude) !== normStr(b.latitude) || normStr(e.longitude) !== normStr(b.longitude);
}

function geoMapsEqual(a: ReturnType<typeof parseGeoForSubmit>, b: ReturnType<typeof parseGeoForSubmit>): boolean {
    return (
        a.lat === b.lat &&
        a.long === b.long &&
        normStr(a.distanceFromRegion) === normStr(b.distanceFromRegion)
    );
}

function openMaps(latStr: string, lonStr: string) {
    const lat = parseFloat(String(latStr).replace(/,/g, '').trim());
    const lng = parseFloat(String(lonStr).replace(/,/g, '').trim());
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank', 'noopener,noreferrer');
}

/** Prefer form state; fall back to Firestore location / legacy latitudeLongitude so the route map always gets coords when stored. */
function routePreviewCoords(
    b: Branch,
    edited: Record<string, unknown> | null | undefined
): { latitude: string; longitude: string } {
    const t = (v: unknown) => String(v ?? '').trim();
    if (edited) {
        const elat = t(edited.latitude);
        const elng = t(edited.longitude);
        if (elat && elng) return { latitude: elat, longitude: elng };
    }
    if (b.location?.lat != null && b.location?.long != null) {
        return { latitude: String(b.location.lat), longitude: String(b.location.long) };
    }
    if (b.latitudeLongitude) {
        const parts = b.latitudeLongitude.split(/[,\s]+/).map((p) => p.trim()).filter(Boolean);
        if (parts.length >= 2) return { latitude: parts[0], longitude: parts[1] };
    }
    return { latitude: t(edited?.latitude), longitude: t(edited?.longitude) };
}

function branchToEditedShape(b: Branch) {
    let latitude = '';
    let longitude = '';
    let distanceFromRegion = '';
    if (b.location && (b.location.lat != null || b.location.long != null)) {
        if (b.location.lat != null) latitude = String(b.location.lat);
        if (b.location.long != null) longitude = String(b.location.long);
        distanceFromRegion = b.location.distanceFromRegion || '';
    } else if (b.latitudeLongitude) {
        const parts = b.latitudeLongitude.split(/[,\s]+/).map((p) => p.trim()).filter(Boolean);
        if (parts.length >= 2) {
            latitude = parts[0];
            longitude = parts[1];
        }
        distanceFromRegion = b.distanceFromRegion || '';
    } else {
        distanceFromRegion = b.distanceFromRegion || '';
    }

    const sr = (b.sraLevel || '').toLowerCase();
    const sraLevel: BranchFormData['sraLevel'] =
        sr === 'green' || sr === 'yellow' || sr === 'orange' || sr === 'red' ? (sr as BranchFormData['sraLevel']) : 'green';

    return {
        ...b,
        mobile: b.mobile || '',
        address: b.address || '',
        locationId: b.locationId || '',
        psoId: b.psoId || '',
        branchCode: b.branchCode || '',
        securityCode: b.securityCode || '',
        name: b.name || '',
        branchType: b.branchType || 'Branch',
        branchTypeOther: b.branchTypeOther || '',
        cashLimitMillion: b.cashLimitMillion != null ? String(b.cashLimitMillion) : '',
        atmAvailable: !!b.atmAvailable,
        atmMountType:
            b.atmMountType === 'standalone' || b.atmMountType === 'wall_mounted' ? b.atmMountType : '',
        atmSeparateFromMainEntrance:
            b.atmSeparateFromMainEntrance !== undefined && b.atmSeparateFromMainEntrance !== null
                ? !!b.atmSeparateFromMainEntrance
                : true,
        vantagePointAvailable: b.vantagePointAvailable !== undefined ? !!b.vantagePointAvailable : true,
        emergencyExitAvailable: b.emergencyExitAvailable !== undefined ? !!b.emergencyExitAvailable : true,
        lockerAvailable: b.lockerAvailable !== undefined ? !!b.lockerAvailable : true,
        coveredArea: b.coveredArea || '',
        landmark: b.landmark || '',
        nearestOtherBanksBranches: b.nearestOtherBanksBranches || '',
        nearestPoliceStationName: b.nearestPoliceStationName || '',
        nearestPoliceStationAddress: b.nearestPoliceStationAddress || '',
        nearestPoliceStationPhone: b.nearestPoliceStationPhone || '',
        latitude,
        longitude,
        distanceFromRegion,
        sraLevel: sraLevel as BranchFormData['sraLevel'],
        sraReason: b.sraReason || '',
        eventAffectedEnabled: !!(b.eventAffectedActive ?? (b.eventAffectedAreasNote && b.eventAffectedAreasNote.trim())),
        eventAffectedAreasNote: b.eventAffectedAreasNote || '',
        saturdayOpen: !!b.saturdayOpen,
        numberOfGuardsDay: typeof b.numberOfGuardsDay === 'number' ? String(b.numberOfGuardsDay) : '',
        numberOfGuardsNight: typeof b.numberOfGuardsNight === 'number' ? String(b.numberOfGuardsNight) : '',
        departments: b.departments || [],
        cdmAvailable: !!b.cdmAvailable,
        accessControlAvailable: !!b.accessControlAvailable,
        accessControlUnits: typeof b.accessControlUnits === 'number' ? String(b.accessControlUnits) : '',
        accessControlLocations: b.accessControlLocations || [],
    };
}

export default function BranchDetailsModal({ isOpen, onClose, branch, managers, bankId, onUpdate, editMode = false }: BranchDetailsModalProps) {
    const gmapsKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const userRole = sessionStorage.getItem('role');
    const isBankUser = userRole === 'bank';
    const isPsoUser = userRole === 'pso';
    const isSuperAdmin = isBankUser; // Bank users are super admins
    const pso = useAppSelector((s) => s.user.pso);
    const isMisIncharge = useMemo(() => designationIsMisIncharge(pso?.designation), [pso]);
    const [isEditing, setIsEditing] = useState(editMode && !isMisIncharge);
    const [loading, setLoading] = useState(false);
    const [editedBranch, setEditedBranch] = useState<any>(null);
    const [customLocationName, setCustomLocationName] = useState('');
    const [editedManagers, setEditedManagers] = useState<Record<string, Partial<Manager>>>({});
    const [mapPickerOpen, setMapPickerOpen] = useState(false);
    const [policeLookupLoading, setPoliceLookupLoading] = useState(false);
    const [accessControlLocationInput, setAccessControlLocationInput] = useState('');
    const [accessControlError, setAccessControlError] = useState('');

    const [departmentsList, setDepartmentsList] = useState<string[]>([]);
    const [departmentSearch, setDepartmentSearch] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [creatingDept, setCreatingDept] = useState(false);
    const [isAddDeptModalOpen, setIsAddDeptModalOpen] = useState(false);
    const [newDeptName, setNewDeptName] = useState('');
    const departmentContainerRef = useRef<HTMLDivElement>(null);

    const [loadingDeployments, setLoadingDeployments] = useState(false);
    const [deploymentsData, setDeploymentsData] = useState<{
        cctv: any[];
        bams: any[];
        guard: any[];
    }>({ cctv: [], bams: [], guard: [] });

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (departmentContainerRef.current && !departmentContainerRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!isOpen || !bankId) return;

        const loadDepartments = async () => {
            try {
                const { collection, getDocs, deleteDoc, doc } = await import('firebase/firestore');
                const deptSnap = await getDocs(collection(db, 'banks', bankId, 'departments'));
                
                const defaultsToDelete = [
                    'Prestige',
                    'CMD',
                    'Currency Exchange',
                    'Prestige + CMD',
                    'Prestige + Currency Exchange'
                ];

                const validDocs: string[] = [];
                for (const d of deptSnap.docs) {
                    const name = d.data().name as string;
                    if (defaultsToDelete.includes(name)) {
                        try {
                            await deleteDoc(doc(db, 'banks', bankId, 'departments', d.id));
                        } catch (e) {
                            console.error('Error deleting legacy seeded department:', e);
                        }
                    } else if (name) {
                        validDocs.push(name);
                    }
                }

                validDocs.sort((a, b) => a.localeCompare(b));
                setDepartmentsList(validDocs);
            } catch (err) {
                console.error('Error loading departments:', err);
            }
        };

        void loadDepartments();
    }, [isOpen, bankId]);

    useEffect(() => {
        if (!isOpen || !branch || !branch.id || !bankId) {
            setDeploymentsData({ cctv: [], bams: [], guard: [] });
            return;
        }

        let active = true;
        setLoadingDeployments(true);

        const fetchDeployments = async () => {
            try {
                const { collection, getDocs, doc, getDoc } = await import('firebase/firestore');
                const { db } = await import('../../../../firebase/config');

                const deploymentsSnap = await getDocs(collection(db, 'banks', bankId, 'companyDeployment'));
                const list = deploymentsSnap.docs
                    .map((d) => ({ id: d.id, ...d.data() } as any))
                    .filter((d) => d.branchId === branch.id);

                const companiesSnap = await getDocs(collection(db, 'banks', bankId, 'companies'));
                const bankCompanies = new Map<string, any>();
                companiesSnap.docs.forEach((doc) => {
                    bankCompanies.set(doc.id, { id: doc.id, ...doc.data() });
                });

                const cctvList: any[] = [];
                const bamsList: any[] = [];
                const guardList: any[] = [];

                for (const d of list) {
                    const bankComp = bankCompanies.get(d.companiesId);
                    if (!bankComp) continue;

                    let companyName = bankComp.companyName || '';
                    let companyType = bankComp.companyType || '';

                    if (bankComp.companyId) {
                        try {
                            const globalDoc = await getDoc(doc(db, 'companies', bankComp.companyId));
                            if (globalDoc.exists()) {
                                const gData = globalDoc.data();
                                companyName = bankComp.companyName || gData.companyName || '';
                                companyType = bankComp.companyType || gData.companyType || '';
                            }
                        } catch (err) {
                            console.warn('Failed to fetch global company info:', err);
                        }
                    }

                    const enriched = {
                        ...d,
                        companyName,
                        companyType
                    };

                    const typeLower = companyType.toLowerCase();
                    if (typeLower.includes('cctv')) {
                        cctvList.push(enriched);
                    } else if (typeLower.includes('bams')) {
                        bamsList.push(enriched);
                    } else if (typeLower.includes('guard')) {
                        guardList.push(enriched);
                    }
                }

                if (active) {
                    setDeploymentsData({
                        cctv: cctvList,
                        bams: bamsList,
                        guard: guardList
                    });
                }
            } catch (err) {
                console.error('Error fetching deployments inside modal:', err);
            } finally {
                if (active) {
                    setLoadingDeployments(false);
                }
            }
        };

        void fetchDeployments();

        return () => {
            active = false;
        };
    }, [isOpen, branch, bankId]);

    const handleCreateDepartment = async () => {
        const trimmed = newDeptName.trim();
        if (!trimmed || !bankId) return;

        if (departmentsList.map(d => d.toLowerCase()).includes(trimmed.toLowerCase())) {
            alert('Department already exists!');
            return;
        }

        setCreatingDept(true);
        try {
            const { addDoc, collection } = await import('firebase/firestore');
            await addDoc(collection(db, 'banks', bankId, 'departments'), { name: trimmed });
            setDepartmentsList(prev => [...prev, trimmed].sort((a, b) => a.localeCompare(b)));
            
            // Also select it immediately
            const currentSelected = editedBranch?.departments || [];
            if (!currentSelected.includes(trimmed)) {
                handleFieldChange('departments', [...currentSelected, trimmed]);
            }

            setNewDeptName('');
            setIsAddDeptModalOpen(false);
        } catch (err) {
            console.error('Error creating department:', err);
        } finally {
            setCreatingDept(false);
        }
    };

    const editBaselineRef = useRef<any>(null);
    const editManagerSnapshotsRef = useRef<
        Record<
            'branchManagerId' | 'operationalManagerId' | 'areaManagerId',
            { name: string; email: string; mobile: string } | null
        >
    >({
        branchManagerId: null,
        operationalManagerId: null,
        areaManagerId: null,
    });
    const prevIsEditingRef = useRef(false);

    const fillNearbyFromCoords = useCallback(
        async (lat: number, lng: number) => {
            const key = gmapsKey?.trim();
            if (!key) return;
            setPoliceLookupLoading(true);
            try {
                const [police, areaLabel, banksCsv] = await Promise.all([
                    fetchNearestPoliceStation(key, lat, lng),
                    fetchNearestAreaLabel(key, lat, lng),
                    fetchNearbyBankNamesCsv(key, lat, lng),
                ]);
                setEditedBranch((prev: any) =>
                    prev
                        ? {
                              ...prev,
                              nearestPoliceStationName: police?.name ?? '',
                              nearestPoliceStationAddress: police?.address ?? '',
                              nearestPoliceStationPhone: police?.phone ?? '',
                              landmark: areaLabel ?? '',
                              nearestOtherBanksBranches: banksCsv ?? '',
                          }
                        : prev
                );
            } finally {
                setPoliceLookupLoading(false);
            }
        },
        [gmapsKey]
    );

    // Reset to view mode when modal closes, or set initial edit mode when opening
    useEffect(() => {
        if (!isOpen) {
            setIsEditing(false);
        } else if (editMode && (isBankUser || isPsoUser) && !isMisIncharge) {
            setIsEditing(true);
        }
    }, [isOpen, editMode, isBankUser, isPsoUser, isMisIncharge]);

    // Simplified state management
    const [locations, setLocations] = useState<LocationOption[]>([]);
    const [allManagers, setAllManagers] = useState<Manager[]>([]);
    const [psos, setPsos] = useState<PSO[]>([]);

    const mapRouteOrigin = useMemo(() => {
        if (!editedBranch?.locationId || editedBranch.locationId === 'custom') return null;
        const loc = locations.find((l) => l.id === editedBranch.locationId);
        const la = loc?.location?.lat;
        const lo = loc?.location?.long;
        if (la == null || lo == null) return null;
        const nla = Number(la);
        const nlo = Number(lo);
        if (Number.isNaN(nla) || Number.isNaN(nlo)) return null;
        return { lat: nla, lng: nlo };
    }, [editedBranch?.locationId, locations]);

    useEffect(() => {
        if (branch) {
            setEditedBranch(branchToEditedShape(branch));
            setCustomLocationName('');
            setEditedManagers({});
        }
    }, [branch, isOpen]);

    useEffect(() => {
        if (isOpen && bankId) {
            const loadData = async () => {
                try {
                    const locSnap = await getDocs(collection(db, 'locations'));
                    const locList = locSnap.docs.map((d) => ({ id: d.id, ...d.data() } as LocationOption));
                    setLocations(locList);

                    const psoSnap = await getDocs(collection(db, 'banks', bankId, 'pos'));
                    const psoList = psoSnap.docs.map(d => ({
                        id: d.id,
                        ...d.data()
                    } as PSO));
                    setPsos(psoList);

                    const mgrSnap = await getDocs(collection(db, 'banks', bankId, 'managers'));
                    const managerList = mgrSnap.docs.map(d => ({
                        id: d.id,
                        ...d.data()
                    } as Manager));
                    setAllManagers(managerList);
                } catch (err) {
                    console.error('Error loading data:', err);
                }
            };
            loadData();
        }
    }, [isOpen, bankId]);

    useEffect(() => {
        if (branch && allManagers.length > 0) {
            const updatedManagers: Record<string, Partial<Manager>> = { ...editedManagers };
            ['branchManagerId', 'operationalManagerId', 'areaManagerId'].forEach(key => {
                const mgrId = branch[key as keyof Branch] as string;
                if (mgrId && !editedManagers[key]) {
                    const foundMgr = allManagers.find(m => m.id === mgrId);
                    if (foundMgr) {
                        updatedManagers[key] = { ...foundMgr };
                    }
                }
            });
            setEditedManagers(updatedManagers);
        }
    }, [allManagers, branch, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            prevIsEditingRef.current = false;
            return;
        }
        if (isEditing && !prevIsEditingRef.current && branch) {
            editBaselineRef.current = branchToEditedShape(branch);
            const snap = (id?: string) => {
                if (!id) return null;
                const m = managers[id] || allManagers.find((x) => x.id === id);
                return m ? { name: m.name || '', email: m.email || '', mobile: m.mobile || '' } : null;
            };
            editManagerSnapshotsRef.current = {
                branchManagerId: snap(branch.branchManagerId),
                operationalManagerId: snap(branch.operationalManagerId),
                areaManagerId: snap(branch.areaManagerId),
            };
        }
        prevIsEditingRef.current = isEditing;
    }, [isOpen, isEditing, branch, managers, allManagers]);

    const lockedFieldsForNonBank = useMemo(
        () =>
            new Set([
                // Only these remain disabled for non-bank users (as requested)
                'name', // Bank
                'branchCode', // Branch Code
                'securityCode', // Client Code
                'locationId', // Region
                'address', // Branch Name
            ] as const),
        []
    );

    const canEditField = useCallback(
        (field: string) => {
            if (isMisIncharge) return false;
            if (!isEditing) return false;
            if (isSuperAdmin) return true;
            if (isPsoUser) return !lockedFieldsForNonBank.has(field as any);
            return false;
        },
        [isEditing, isSuperAdmin, isPsoUser, lockedFieldsForNonBank, isMisIncharge]
    );

    useEffect(() => {
        if (!isOpen || !branch) return;
        if (!isEditing || !editedBranch) return;
        if (!canEditField('distanceFromRegion')) return;

        const id = editedBranch.locationId;
        if (!id || id === 'custom') return;
        const loc = locations.find((l) => l.id === id);
        const refLat = loc?.location?.lat;
        const refLng = loc?.location?.long;
        if (refLat == null || refLng == null) return;
        const rLat = Number(refLat);
        const rLng = Number(refLng);
        if (Number.isNaN(rLat) || Number.isNaN(rLng)) return;

        const bLat = parseFloat(String(editedBranch.latitude ?? '').replace(/,/g, '').trim());
        const bLng = parseFloat(String(editedBranch.longitude ?? '').replace(/,/g, '').trim());
        if (Number.isNaN(bLat) || Number.isNaN(bLng)) return;

        const fallbackText = formatDistanceKm(haversineDistanceKm(rLat, rLng, bLat, bLng));
        const key = gmapsKey?.trim();

        let cancelled = false;
        const tid = window.setTimeout(() => {
            void (async () => {
                if (!key) {
                    if (cancelled) return;
                    setEditedBranch((prev: any) => {
                        if (!prev || prev.distanceFromRegion === fallbackText) return prev;
                        return { ...prev, distanceFromRegion: fallbackText };
                    });
                    return;
                }
                try {
                    const meters = await fetchDrivingDistanceMeters(key, { lat: rLat, lng: rLng }, { lat: bLat, lng: bLng });
                    if (cancelled) return;
                    const text = meters != null ? formatDrivingKmLabel(meters) : fallbackText;
                    setEditedBranch((prev: any) => {
                        if (!prev || prev.distanceFromRegion === text) return prev;
                        return { ...prev, distanceFromRegion: text };
                    });
                } catch {
                    if (cancelled) return;
                    setEditedBranch((prev: any) => {
                        if (!prev || prev.distanceFromRegion === fallbackText) return prev;
                        return { ...prev, distanceFromRegion: fallbackText };
                    });
                }
            })();
        }, 450);

        return () => {
            cancelled = true;
            window.clearTimeout(tid);
        };
    }, [
        isOpen,
        branch,
        isEditing,
        editedBranch?.locationId,
        editedBranch?.latitude,
        editedBranch?.longitude,
        locations,
        gmapsKey,
        canEditField,
    ]);

    const fieldClass = useCallback(
        (editable: boolean, extra?: string) =>
            [
                editable ? 'form-control' : 'form-control form-control-readonly !cursor-not-allowed',
                extra || '',
            ].join(' '),
        []
    );

    if (!branch) return null;

    const handleFieldChange = (field: string, value: any) => {
        setEditedBranch((prev: any) => (prev ? { ...prev, [field]: value } : null));
    };

    const handleAddDepartment = (name: string) => {
        const trimmed = name.trim();
        if (!trimmed) return;

        const currentSelected = editedBranch?.departments || [];
        if (!currentSelected.includes(trimmed)) {
            handleFieldChange('departments', [...currentSelected, trimmed]);
        }

        setDepartmentSearch('');
        setIsDropdownOpen(false);
    };

    const handleRemoveDepartment = (name: string) => {
        const currentSelected = editedBranch?.departments || [];
        handleFieldChange('departments', currentSelected.filter((dept: string) => dept !== name));
    };

    const handleSave = async () => {
        if (!editedBranch || !bankId || !branch) return;

        if (canEditField('sraLevel')) {
            const sl = editedBranch.sraLevel;
            if (
                (sl === 'orange' || sl === 'red') &&
                !String(editedBranch.sraReason || '').trim()
            ) {
                alert('Please enter a reason for Orange or Red SRA level.');
                return;
            }
        }

        if (editedBranch?.accessControlAvailable && accessControlLocationInput.trim() !== '') {
            alert('Please press Enter to add the Access Control Location before saving.');
            return;
        }

        const b = editBaselineRef.current ?? branchToEditedShape(branch);
        const e = editedBranch;

        setLoading(true);
        try {
            const branchRef = doc(db, 'banks', bankId, 'branch', branch.id);
            const updateData: Record<string, unknown> = {};

            if (canEditField('mobile') && normStr(e.mobile) !== normStr(b.mobile)) {
                updateData.mobile = normStr(e.mobile);
            }
            if (canEditField('address') && normStr(e.address) !== normStr(b.address)) {
                updateData.address = normStr(e.address);
            }

            if (canEditField('branchCode') && normStr(e.branchCode) !== normStr(b.branchCode)) {
                updateData.branchCode = normStr(e.branchCode);
            }
            if (canEditField('securityCode') && normStr(e.securityCode) !== normStr(b.securityCode)) {
                updateData.securityCode = normStr(e.securityCode);
            }
            if (canEditField('name') && normStr(e.name) !== normStr(b.name)) {
                updateData.name = normStr(e.name);
            }

            if (canEditField('psoId') && normMgrId(e.psoId) !== normMgrId(b.psoId)) {
                updateData.psoId = normMgrId(e.psoId) || null;
            }

            if (canEditField('locationId') && e.locationId === 'custom' && customLocationName.trim()) {
                const { addDoc, collection: col } = await import('firebase/firestore');
                const locRef = await addDoc(col(db, 'locations'), {
                    name: customLocationName.trim(),
                });
                const newLocId = locRef.id;
                if (newLocId !== normStr(b.locationId)) {
                    updateData.locationId = newLocId;
                }
            } else if (canEditField('locationId') && e.locationId && e.locationId !== 'custom') {
                if (normStr(e.locationId) !== normStr(b.locationId)) {
                    updateData.locationId = e.locationId;
                }
            }

            const mgrKeys = ['branchManagerId', 'operationalManagerId', 'areaManagerId'] as const;
            for (const k of mgrKeys) {
                if (!canEditField(k)) continue;
                if (normMgrId(e[k]) !== normMgrId(b[k])) {
                    updateData[k] = normMgrId(e[k]) || null;
                }
            }

            if (isSuperAdmin || isPsoUser) {
                // PSO editable subset (and also available to super admin)
                if (canEditField('cashLimitMillion')) {
                    const cE = cashToNumberOrNull(e.cashLimitMillion);
                    const cB = cashToNumberOrNull(b.cashLimitMillion);
                    if (cE !== cB) {
                        if (cE != null) {
                            updateData.cashLimitMillion = cE;
                        } else {
                            updateData.cashLimitMillion = deleteField();
                        }
                    }
                }

                if (canEditField('numberOfGuardsDay')) {
                    const gE = nonNegativeIntOrNull(e.numberOfGuardsDay);
                    const gB = nonNegativeIntOrNull(b.numberOfGuardsDay);
                    if (gE !== gB) {
                        if (gE != null) {
                            updateData.numberOfGuardsDay = gE;
                        } else {
                            updateData.numberOfGuardsDay = deleteField();
                        }
                    }
                }
                if (canEditField('numberOfGuardsNight')) {
                    const gE = nonNegativeIntOrNull(e.numberOfGuardsNight);
                    const gB = nonNegativeIntOrNull(b.numberOfGuardsNight);
                    if (gE !== gB) {
                        if (gE != null) {
                            updateData.numberOfGuardsNight = gE;
                        } else {
                            updateData.numberOfGuardsNight = deleteField();
                        }
                    }
                }

                if (canEditField('atmAvailable')) {
                    const atmE = !!e.atmAvailable;
                    const atmB = !!b.atmAvailable;
                    const atmSubDirty =
                        atmE &&
                        (normStr(e.atmMountType) !== normStr(b.atmMountType) ||
                            !!e.atmSeparateFromMainEntrance !== !!b.atmSeparateFromMainEntrance);
                    if (atmE !== atmB || atmSubDirty) {
                        updateData.atmAvailable = atmE;
                        if (atmE) {
                            updateData.atmMountType = e.atmMountType || null;
                            updateData.atmSeparateFromMainEntrance = !!e.atmSeparateFromMainEntrance;
                        } else {
                            updateData.atmMountType = null;
                            updateData.atmSeparateFromMainEntrance = null;
                        }
                    }
                }

                if (canEditField('vantagePointAvailable') && !!e.vantagePointAvailable !== !!b.vantagePointAvailable) {
                    updateData.vantagePointAvailable = !!e.vantagePointAvailable;
                }
                if (
                    canEditField('emergencyExitAvailable') &&
                    !!e.emergencyExitAvailable !== !!b.emergencyExitAvailable
                ) {
                    updateData.emergencyExitAvailable = !!e.emergencyExitAvailable;
                }
                if (canEditField('lockerAvailable') && !!e.lockerAvailable !== !!b.lockerAvailable) {
                    updateData.lockerAvailable = !!e.lockerAvailable;
                }
                if (canEditField('saturdayOpen') && !!e.saturdayOpen !== !!b.saturdayOpen) {
                    updateData.saturdayOpen = !!e.saturdayOpen;
                }
                if (canEditField('cdmAvailable') && !!e.cdmAvailable !== !!b.cdmAvailable) {
                    updateData.cdmAvailable = !!e.cdmAvailable;
                }
                if (canEditField('departments') && JSON.stringify(e.departments) !== JSON.stringify(b.departments)) {
                    updateData.departments = e.departments || [];
                }

                if (canEditField('accessControlAvailable')) {
                    const acE = !!e.accessControlAvailable;
                    const acB = !!b.accessControlAvailable;
                    const auE = nonNegativeIntOrNull(e.accessControlUnits);
                    const auB = nonNegativeIntOrNull(b.accessControlUnits);
                    const acSubDirty = acE && (auE !== auB || JSON.stringify(e.accessControlLocations) !== JSON.stringify(b.accessControlLocations));
                    if (acE !== acB || acSubDirty) {
                        updateData.accessControlAvailable = acE;
                        if (acE) {
                            if (auE != null) updateData.accessControlUnits = auE;
                            else updateData.accessControlUnits = deleteField();
                            updateData.accessControlLocations = e.accessControlLocations || [];
                        } else {
                            updateData.accessControlUnits = deleteField();
                            updateData.accessControlLocations = deleteField();
                        }
                    }
                }

                const optStr = (key: keyof typeof e, bkey: keyof typeof b = key) => {
                    if (!canEditField(key as string)) return;
                    if (normStr(e[key]) !== normStr(b[bkey])) {
                        updateData[key as string] = normStr(e[key]) ? normStr(e[key]) : deleteField();
                    }
                };
                optStr('coveredArea');
                optStr('landmark');
                optStr('nearestOtherBanksBranches');
                optStr('nearestPoliceStationName');
                optStr('nearestPoliceStationAddress');
                optStr('nearestPoliceStationPhone');

                if (canEditField('sraLevel')) {
                    const sraE = e.sraLevel || 'green';
                    const sraB = b.sraLevel || 'green';
                    if (sraE !== sraB) {
                        updateData.sraLevel = sraE;
                    }
                    const reasonE = normStr(e.sraReason);
                    const reasonB = normStr(b.sraReason);
                    if (sraE === 'orange' || sraE === 'red') {
                        if (reasonE !== reasonB || sraE !== sraB) {
                            updateData.sraReason = reasonE || null;
                        }
                    } else if (reasonB || sraE !== sraB) {
                        updateData.sraReason = deleteField();
                    }
                }

                if (canEditField('eventAffectedEnabled')) {
                    const evE = !!e.eventAffectedEnabled;
                    const evB = !!b.eventAffectedEnabled;
                    const noteE = normStr(e.eventAffectedAreasNote);
                    const noteB = normStr(b.eventAffectedAreasNote);
                    if (evE !== evB) {
                        updateData.eventAffectedActive = evE;
                    }
                    if (noteE !== noteB || evE !== evB) {
                        if (evE && noteE) {
                            updateData.eventAffectedAreasNote = noteE;
                        } else {
                            updateData.eventAffectedAreasNote = deleteField();
                        }
                    }
                }
            }

            if (canEditField('branchType')) {
                const btE = e.branchType || 'Branch';
                const btB = b.branchType || 'Branch';
                if (btE !== btB) {
                    updateData.branchType = btE;
                }
                const otherE = btE === 'Other' ? normStr(e.branchTypeOther) : '';
                const otherB = btB === 'Other' ? normStr(b.branchTypeOther) : '';
                if (otherE !== otherB || btE !== btB) {
                    if (btE === 'Other' && normStr(e.branchTypeOther)) {
                        updateData.branchTypeOther = normStr(e.branchTypeOther);
                    } else {
                        updateData.branchTypeOther = deleteField();
                    }
                }

                if (canEditField('latitude') || canEditField('longitude') || canEditField('distanceFromRegion')) {
                    if (branchCoordsDiffer(e, b)) {
                        updateData.location = parseGeoForSubmit(e as BranchFormData);
                        updateData.latitudeLongitude = deleteField();
                        updateData.distanceFromRegion = deleteField();
                    }
                }
            }

            if (Object.keys(updateData).length > 0) {
                await updateDoc(branchRef, updateData as any);
            }

            if (isSuperAdmin) {
                const snaps = editManagerSnapshotsRef.current;
                for (const k of mgrKeys) {
                    const managerId = normMgrId(e[k]);
                    if (!managerId) continue;
                    const patch = editedManagers[k];
                    const snap = snaps[k];
                    if (!patch || !normStr(patch.name)) continue;
                    const base =
                        snap ||
                        (() => {
                            const m = allManagers.find((x) => x.id === managerId);
                            return m
                                ? { name: m.name || '', email: m.email || '', mobile: m.mobile || '' }
                                : null;
                        })();
                    if (!base) continue;
                    const managerUpdateData: Record<string, string> = {};
                    if (normStr(patch.name) !== normStr(base.name)) {
                        managerUpdateData.name = normStr(patch.name);
                    }
                    if (patch.email !== undefined && normStr(patch.email) !== normStr(base.email)) {
                        managerUpdateData.email = normStr(patch.email);
                    }
                    if (patch.mobile !== undefined && normStr(patch.mobile) !== normStr(base.mobile)) {
                        managerUpdateData.mobile = normStr(patch.mobile);
                    }
                    if (Object.keys(managerUpdateData).length > 0) {
                        await updateDoc(doc(db, 'banks', bankId, 'managers', managerId), managerUpdateData);
                    }
                }
            }

            setIsEditing(false);
            if (onUpdate) onUpdate();
            onClose();
        } catch (err: any) {
            console.error('Error updating branch:', err);
            alert('Failed to update branch: ' + (err.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        // Reset to original values
        if (branch) {
            setEditedBranch(branchToEditedShape(branch));
            const initialManagers: Record<string, Partial<Manager>> = {};
            if (branch.branchManagerId && managers[branch.branchManagerId]) {
                initialManagers.branchManagerId = { ...managers[branch.branchManagerId] };
            }
            if (branch.operationalManagerId && managers[branch.operationalManagerId]) {
                initialManagers.operationalManagerId = { ...managers[branch.operationalManagerId] };
            }
            if (branch.areaManagerId && managers[branch.areaManagerId]) {
                initialManagers.areaManagerId = { ...managers[branch.areaManagerId] };
            }
            setEditedManagers(initialManagers);
        }
        setIsEditing(false);
    };

    const handleClose = () => {
        // If editing, cancel first to reset changes
        if (isEditing) {
            handleCancel();
        }
        onClose();
    };

    const managerFields = [
        { key: 'branchManagerId', label: 'Branch Manager' },
        { key: 'operationalManagerId', label: 'Manager Operation' },
        { key: 'areaManagerId', label: 'Area Operation Manager' },
    ] as const;

    const profileEdit = isEditing;
    const managerEdit = isEditing;
    const mapPinCoords = editedBranch ? routePreviewCoords(branch, editedBranch) : { latitude: '', longitude: '' };

    return (
        <>
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Branch Details"
            size="wide"
            closeOnBackdropClick={false}
        >
            <div className="p-4 sm:p-6">
                {/* Edit Button in Header Area - Bank users or PSO (limited) */}
                {(isSuperAdmin || isPsoUser) && !isMisIncharge && !isEditing && (
                    <div className="mb-4 flex justify-end">
                        <button
                            onClick={() => setIsEditing(true)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-400/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-500 dark:hover:bg-gray-700 dark:hover:text-white dark:focus:ring-gray-500/25"
                            aria-label="Edit branch details"
                        >
                            <Icon icon={FiEdit2} className="h-4 w-4" />
                        </button>
                    </div>
                )}
                <div className="space-y-4">
                    {/* Same grid order as Add Branch */}
                    <FormRow cols={4}>
                        <FormCell>
                            <label className="form-field-label">Bank</label>
                            <input
                                type="text"
                                readOnly={!canEditField('name')}
                                value={editedBranch?.name ?? branch?.name ?? 'N/A'}
                                onChange={(e) => handleFieldChange('name', e.target.value)}
                                className={
                                    canEditField('name') ? 'form-control' : 'form-control form-control-readonly'
                                }
                            />
                        </FormCell>
                        <FormCell>
                            <TextField
                                label="Branch code"
                                value={editedBranch?.branchCode ?? ''}
                                readOnly={!canEditField('branchCode')}
                                onChange={(e) => handleFieldChange('branchCode', e.target.value)}
                                placeholder="Code"
                                prefix={<Icon icon={FiHash} className="text-gray-400" />}
                                className={!canEditField('branchCode') && isEditing ? '!cursor-not-allowed' : undefined}
                            />
                        </FormCell>
                        <FormCell>
                            <TextField
                                label="Client code"
                                value={editedBranch?.securityCode ?? ''}
                                readOnly={!canEditField('securityCode')}
                                onChange={(e) => handleFieldChange('securityCode', e.target.value)}
                                placeholder="Client code"
                                prefix={<Icon icon={FiLock} className="text-gray-400" />}
                                className={!canEditField('securityCode') && isEditing ? '!cursor-not-allowed' : undefined}
                            />
                        </FormCell>
                        <FormCell>
                            <TextField
                                label="Branch Contact No."
                                type="tel"
                                value={editedBranch?.mobile ?? ''}
                                readOnly={!canEditField('mobile')}
                                onChange={(e) => handleFieldChange('mobile', e.target.value)}
                                placeholder="Branch Contact No."
                                prefix={<Icon icon={FiPhone} className="text-gray-400" />}
                                className={!canEditField('mobile') && isEditing ? '!cursor-not-allowed' : undefined}
                            />
                        </FormCell>
                    </FormRow>

                    <FormRow cols={4}>
                        <FormCell>
                            <label className="form-field-label">Branch type</label>
                            {canEditField('branchType') ? (
                                <select
                                    value={editedBranch?.branchType || 'Branch'}
                                    onChange={(e) => handleFieldChange('branchType', e.target.value)}
                                    className="form-control"
                                >
                                    {BRANCH_TYPE_OPTIONS.map((t) => (
                                        <option key={t} value={t}>
                                            {t}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    readOnly
                                    value={
                                        (editedBranch?.branchType || branch?.branchType || 'Branch') +
                                        (editedBranch?.branchType === 'Other' && editedBranch?.branchTypeOther
                                            ? ` — ${editedBranch.branchTypeOther}`
                                            : branch?.branchType === 'Other' && branch?.branchTypeOther
                                              ? ` — ${branch.branchTypeOther}`
                                              : '')
                                    }
                                    className="form-control form-control-readonly"
                                />
                            )}
                        </FormCell>
                        <FormCell>
                            <TextField
                                label="Cash limit (M PKR)"
                                value={editedBranch?.cashLimitMillion ?? ''}
                                readOnly={!canEditField('cashLimitMillion')}
                                onChange={(e) => handleFieldChange('cashLimitMillion', e.target.value)}
                                placeholder="e.g. 2.5"
                                className={!canEditField('cashLimitMillion') && isEditing ? '!cursor-not-allowed' : undefined}
                            />
                        </FormCell>
                        <FormCell>
                            <label className="form-field-label">Region</label>
                            <div className="relative">
                                <Icon
                                    icon={FiMapPin}
                                    className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                                />
                                {canEditField('locationId') ? (
                                    <select
                                        value={editedBranch?.locationId ?? branch?.locationId ?? ''}
                                        onChange={(e) => handleFieldChange('locationId', e.target.value)}
                                        className="form-control pl-8"
                                    >
                                        <option value="">Select region</option>
                                        {locations.map((loc) => (
                                            <option key={loc.id} value={loc.id}>
                                                {loc.name}
                                            </option>
                                        ))}
                                        <option value="custom">+ Custom region</option>
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        readOnly
                                        value={
                                            locations.find((l) => l.id === branch.locationId)?.name ||
                                            branch?.locationName ||
                                            'N/A'
                                        }
                                        className="form-control form-control-readonly pl-8"
                                    />
                                )}
                            </div>
                        </FormCell>
                        <FormCell>
                            <label className="form-field-label">Branch name</label>
                            <input
                                type="text"
                                readOnly={!canEditField('address')}
                                value={editedBranch?.address ?? branch?.address ?? ''}
                                onChange={(e) => handleFieldChange('address', e.target.value)}
                                placeholder="Branch name"
                                className={fieldClass(canEditField('address'))}
                            />
                        </FormCell>
                    </FormRow>

                    <FormRow cols={4}>
                        <FormCell className="sm:col-span-2 lg:col-span-1">
                            <TextField
                                label="Number of guards (day)"
                                type="text"
                                inputMode="numeric"
                                value={editedBranch?.numberOfGuardsDay ?? ''}
                                readOnly={!canEditField('numberOfGuardsDay')}
                                onChange={(e) => handleFieldChange('numberOfGuardsDay', e.target.value)}
                                placeholder="e.g. 2"
                                className={!canEditField('numberOfGuardsDay') && isEditing ? '!cursor-not-allowed' : undefined}
                            />
                        </FormCell>
                        <FormCell className="sm:col-span-2 lg:col-span-1">
                            <TextField
                                label="Number of guards (night)"
                                type="text"
                                inputMode="numeric"
                                value={editedBranch?.numberOfGuardsNight ?? ''}
                                readOnly={!canEditField('numberOfGuardsNight')}
                                onChange={(e) => handleFieldChange('numberOfGuardsNight', e.target.value)}
                                placeholder="e.g. 1"
                                className={!canEditField('numberOfGuardsNight') && isEditing ? '!cursor-not-allowed' : undefined}
                            />
                        </FormCell>
                    </FormRow>

                    {canEditField('branchTypeOther') && editedBranch?.branchType === 'Other' && (
                        <FormRow cols={4}>
                            <FormCell className="lg:col-span-2">
                                <TextField
                                    label="Other branch type"
                                    value={editedBranch?.branchTypeOther || ''}
                                    onChange={(e) => handleFieldChange('branchTypeOther', e.target.value)}
                                    placeholder="Specify"
                                />
                            </FormCell>
                        </FormRow>
                    )}

                    {canEditField('locationId') && editedBranch?.locationId === 'custom' && (
                        <FormRow cols={4}>
                            <FormCell className="lg:col-span-2">
                                <TextField
                                    label="Custom region"
                                    value={customLocationName}
                                    onChange={(e) => setCustomLocationName(e.target.value)}
                                    placeholder="Region name"
                                />
                            </FormCell>
                        </FormRow>
                    )}

                    <FormRow cols={4}>
                        <FormCell>
                            <YesNoSwitch
                                label="Vantage point"
                                value={!!editedBranch?.vantagePointAvailable}
                                onChange={(v) => handleFieldChange('vantagePointAvailable', v)}
                                disabled={!canEditField('vantagePointAvailable')}
                            />
                        </FormCell>
                        <FormCell>
                            <YesNoSwitch
                                label="Emergency exit"
                                value={!!editedBranch?.emergencyExitAvailable}
                                onChange={(v) => handleFieldChange('emergencyExitAvailable', v)}
                                disabled={!canEditField('emergencyExitAvailable')}
                            />
                        </FormCell>
                        <FormCell>
                            <YesNoSwitch
                                label="Saturday open"
                                value={!!editedBranch?.saturdayOpen}
                                onChange={(v) => handleFieldChange('saturdayOpen', v)}
                                disabled={!canEditField('saturdayOpen')}
                            />
                        </FormCell>
                        <FormCell>
                            <YesNoSwitch
                                label="Locker"
                                value={!!editedBranch?.lockerAvailable}
                                onChange={(v) => handleFieldChange('lockerAvailable', v)}
                                disabled={!canEditField('lockerAvailable')}
                            />
                        </FormCell>
                    </FormRow>

                    <FormRow cols={4}>
                        <FormCell className="flex flex-col justify-end">
                            <YesNoSwitch
                                label="ATM"
                                value={!!editedBranch?.atmAvailable}
                                onChange={(v) =>
                                    setEditedBranch((prev: any) =>
                                        prev
                                            ? {
                                                  ...prev,
                                                  atmAvailable: v,
                                                  ...(!v
                                                      ? { atmMountType: '', atmSeparateFromMainEntrance: true }
                                                      : {}),
                                              }
                                            : null
                                    )
                                }
                                disabled={!canEditField('atmAvailable')}
                            />
                        </FormCell>
                        <FormCell className="flex flex-col justify-end">
                            <div
                                className={
                                    editedBranch?.atmAvailable
                                        ? ''
                                        : 'invisible pointer-events-none select-none'
                                }
                                aria-hidden={!editedBranch?.atmAvailable}
                            >
                                <label className="form-field-label">ATM type</label>
                                {canEditField('atmMountType') ? (
                                    <select
                                        value={editedBranch?.atmMountType || ''}
                                        onChange={(e) => handleFieldChange('atmMountType', e.target.value)}
                                    className="form-control"
                                        tabIndex={editedBranch?.atmAvailable ? 0 : -1}
                                    >
                                        <option value="">—</option>
                                        <option value="wall_mounted">Wall mounted</option>
                                        <option value="standalone">Standalone</option>
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        readOnly
                                        className="form-control form-control-readonly text-sm"
                                        value={
                                            editedBranch?.atmMountType === 'wall_mounted'
                                                ? 'Wall mounted'
                                                : editedBranch?.atmMountType === 'standalone'
                                                  ? 'Standalone'
                                                  : '—'
                                        }
                                    />
                                )}
                            </div>
                        </FormCell>
                        <FormCell className="flex flex-col justify-end">
                            <div
                                className={
                                    editedBranch?.atmAvailable
                                        ? ''
                                        : 'invisible pointer-events-none select-none'
                                }
                                aria-hidden={!editedBranch?.atmAvailable}
                            >
                                <YesNoSwitch
                                    label="Separate entrance"
                                    value={!!editedBranch?.atmSeparateFromMainEntrance}
                                    onChange={(v) => handleFieldChange('atmSeparateFromMainEntrance', v)}
                                    disabled={!canEditField('atmSeparateFromMainEntrance')}
                                />
                            </div>
                        </FormCell>
                        <FormCell className="flex flex-col justify-end">
                            <YesNoSwitch
                                label="Cash Deposit Machine (CDM)"
                                value={!!editedBranch?.cdmAvailable}
                                onChange={(v) => handleFieldChange('cdmAvailable', v)}
                                disabled={!canEditField('cdmAvailable')}
                            />
                        </FormCell>
                    </FormRow>

                    <FormRow cols={4}>
                        <FormCell className="flex flex-col justify-end">
                            <YesNoSwitch
                                label="Access Control"
                                value={!!editedBranch?.accessControlAvailable}
                                onChange={(v) =>
                                    setEditedBranch((prev: any) =>
                                        prev
                                            ? {
                                                  ...prev,
                                                  accessControlAvailable: v,
                                                  ...(v ? {} : { accessControlUnits: '', accessControlLocations: [] }),
                                              }
                                            : null
                                    )
                                }
                                disabled={!canEditField('accessControlAvailable')}
                            />
                        </FormCell>
                        <FormCell className="flex flex-col justify-end">
                            <div
                                className={
                                    editedBranch?.accessControlAvailable
                                        ? ''
                                        : 'invisible pointer-events-none select-none'
                                }
                                aria-hidden={!editedBranch?.accessControlAvailable}
                            >
                                {isEditing ? (
                                    <TextField
                                        label="Quantity of Units"
                                        type="text"
                                        inputMode="numeric"
                                        value={editedBranch?.accessControlUnits ?? ''}
                                        onChange={(e) => handleFieldChange('accessControlUnits', e.target.value)}
                                        placeholder="e.g. 2"
                                        tabIndex={editedBranch?.accessControlAvailable ? 0 : -1}
                                    />
                                ) : (
                                    <div>
                                        <label className="form-field-label">Quantity of Units</label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={editedBranch?.accessControlUnits || '—'}
                                            className="form-control form-control-readonly text-sm"
                                        />
                                    </div>
                                )}
                            </div>
                        </FormCell>
                        <FormCell className="lg:col-span-2">
                            <div
                                className={
                                    editedBranch?.accessControlAvailable
                                        ? ''
                                        : 'invisible pointer-events-none select-none'
                                }
                                aria-hidden={!editedBranch?.accessControlAvailable}
                            >
                                <label className="form-field-label">Installation Locations</label>
                                {isEditing && canEditField('accessControlLocations') ? (
                                    <>
                                        <div className="relative flex items-center mt-1">
                                            <input
                                                type="text"
                                                placeholder="Type and press Enter to add..."
                                                value={accessControlLocationInput}
                                                onChange={(e) => {
                                                    setAccessControlLocationInput(e.target.value);
                                                    if (accessControlError) setAccessControlError('');
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        const trimmed = accessControlLocationInput.trim();
                                                        if (trimmed) {
                                                            const current = editedBranch?.accessControlLocations || [];
                                                            if (!current.includes(trimmed)) {
                                                                handleFieldChange('accessControlLocations', [...current, trimmed]);
                                                            }
                                                            setAccessControlLocationInput('');
                                                        }
                                                    }
                                                }}
                                                className={`form-control w-full ${accessControlError ? '!border-red-400' : ''}`}
                                                tabIndex={editedBranch?.accessControlAvailable ? 0 : -1}
                                            />
                                        </div>
                                        {accessControlError && (
                                            <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">{accessControlError}</p>
                                        )}
                                        {(editedBranch?.accessControlLocations || []).length > 0 && (
                                            <div className="mt-2.5 flex flex-wrap gap-2">
                                                {(editedBranch?.accessControlLocations || []).map((loc: string) => (
                                                    <span
                                                        key={loc}
                                                        className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-700 dark:bg-blue-500/10 dark:text-blue-400 border border-blue-200/50 dark:border-blue-500/20"
                                                    >
                                                        {loc}
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const current = editedBranch?.accessControlLocations || [];
                                                                handleFieldChange('accessControlLocations', current.filter((l: string) => l !== loc));
                                                            }}
                                                            className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-blue-500 hover:bg-blue-100 hover:text-blue-700 dark:text-blue-400 dark:hover:bg-blue-500/20"
                                                        >
                                                            <Icon icon={FiX} className="h-3 w-3" />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="mt-1.5 flex flex-wrap gap-2">
                                        {(editedBranch?.accessControlLocations || []).length > 0 ? (
                                            (editedBranch?.accessControlLocations || []).map((loc: string) => (
                                                <span
                                                    key={loc}
                                                    className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-700 dark:bg-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-700"
                                                >
                                                    {loc}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="text-sm text-gray-500 dark:text-gray-400 italic">No locations added</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </FormCell>
                    </FormRow>

                    <FormRow cols={3}>
                        <FormCell className="lg:col-span-2">
                            <div className="w-full relative">
                                <div className="flex justify-between items-center mb-1">
                                    <label className="form-field-label !mb-0">Branch Department</label>
                                    {isEditing && (
                                        <button
                                            type="button"
                                            onClick={() => setIsAddDeptModalOpen(true)}
                                            className="text-xs font-bold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 flex items-center gap-1"
                                        >
                                            <Icon icon={FiPlus} className="h-3 w-3" />
                                            Add Department
                                        </button>
                                    )}
                                </div>
                                {isEditing ? (
                                    <>
                                        <div className="relative mt-1" ref={departmentContainerRef}>
                                            <div className="relative flex items-center">
                                                <input
                                                    type="text"
                                                    placeholder="Select department..."
                                                    value={departmentSearch}
                                                    onChange={(e) => {
                                                        setDepartmentSearch(e.target.value);
                                                        setIsDropdownOpen(true);
                                                    }}
                                                    onFocus={() => setIsDropdownOpen(true)}
                                                    className="form-control pr-10 w-full"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                                >
                                                    <Icon icon={FiChevronDown} className={`h-4 w-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                                                </button>
                                            </div>

                                            {isDropdownOpen && (
                                                <ul className="absolute left-0 right-0 z-40 mt-1 max-h-60 overflow-y-auto rounded-xl border border-gray-200 bg-white py-1.5 shadow-xl outline-none dark:border-gray-700 dark:bg-gray-800">
                                                    {departmentsList.filter(dept => 
                                                        dept.toLowerCase().includes(departmentSearch.toLowerCase())
                                                    ).length > 0 ? (
                                                        departmentsList.filter(dept => 
                                                            dept.toLowerCase().includes(departmentSearch.toLowerCase())
                                                        ).map((opt) => {
                                                            const isSelected = (editedBranch?.departments || []).includes(opt);
                                                            return (
                                                                <li key={opt}>
                                                                    <button
                                                                        type="button"
                                                                        disabled={isSelected}
                                                                        onClick={() => handleAddDepartment(opt)}
                                                                        className={`flex w-full items-center justify-between px-4 py-2 text-left text-sm transition ${
                                                                            isSelected 
                                                                                ? 'bg-gray-50 text-gray-400 cursor-not-allowed dark:bg-gray-900/40 dark:text-gray-500' 
                                                                                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700/60'
                                                                        }`}
                                                                    >
                                                                        <span>{opt}</span>
                                                                        {isSelected && <span className="text-xs font-semibold text-gray-400">Selected</span>}
                                                                    </button>
                                                                </li>
                                                            );
                                                        })
                                                    ) : (
                                                        <li className="px-4 py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                                                            No department found
                                                        </li>
                                                    )}
                                                </ul>
                                            )}
                                        </div>

                                        {/* Badges list with X close buttons */}
                                        {(editedBranch?.departments || []).length > 0 && (
                                            <div className="mt-2.5 flex flex-wrap gap-2">
                                                {(editedBranch?.departments || []).map((dept: string) => (
                                                    <span
                                                        key={dept}
                                                        className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-500/20"
                                                    >
                                                        {dept}
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveDepartment(dept)}
                                                            className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-emerald-500 hover:bg-emerald-100 hover:text-emerald-700 dark:text-emerald-400 dark:hover:bg-emerald-500/20"
                                                        >
                                                            <Icon icon={FiX} className="h-3 w-3" />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    /* Read-only Badge display in View mode */
                                    <div className="mt-1.5 flex flex-wrap gap-2">
                                        {(editedBranch?.departments || []).length > 0 ? (
                                            (editedBranch?.departments || []).map((dept: string) => (
                                                <span
                                                    key={dept}
                                                    className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-700 dark:bg-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-700"
                                                >
                                                    {dept}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="text-sm text-gray-500 dark:text-gray-400 italic">No department found</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </FormCell>
                    </FormRow>

                    <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6 lg:items-end">
                        <FormCell className="min-w-0 sm:col-span-2 lg:col-span-2">
                            {canEditField('latitude') || canEditField('longitude') ? (
                                <PakistanPlaceSearch
                                    apiKey={gmapsKey}
                                    onResolved={(p) => {
                                        handleFieldChange('latitude', String(p.lat));
                                        handleFieldChange('longitude', String(p.lng));
                                        void fillNearbyFromCoords(p.lat, p.lng);
                                    }}
                                />
                            ) : (
                                <div>
                                    <label className="form-field-label">Find location (Pakistan)</label>
                                    <input
                                        type="text"
                                        readOnly
                                        value="—"
                                        className="form-control form-control-readonly text-sm"
                                    />
                                </div>
                            )}
                        </FormCell>
                        <FormCell>
                            <label className="form-field-label">Long</label>
                            <input
                                type="text"
                                inputMode="decimal"
                                readOnly={!canEditField('longitude')}
                                value={editedBranch?.longitude ?? ''}
                                onChange={(e) => handleFieldChange('longitude', e.target.value)}
                                className={fieldClass(canEditField('longitude'), 'text-sm')}
                            />
                        </FormCell>
                        <FormCell>
                            <label className="form-field-label">Lat</label>
                            <input
                                type="text"
                                inputMode="decimal"
                                readOnly={!canEditField('latitude')}
                                value={editedBranch?.latitude ?? ''}
                                onChange={(e) => handleFieldChange('latitude', e.target.value)}
                                className={fieldClass(canEditField('latitude'), 'text-sm')}
                            />
                        </FormCell>
                        <FormCell>
                            <label className="form-field-label">Distance from region</label>
                            <input
                                type="text"
                                readOnly={!canEditField('distanceFromRegion')}
                                value={editedBranch?.distanceFromRegion || ''}
                                onChange={(e) => handleFieldChange('distanceFromRegion', e.target.value)}
                                placeholder={canEditField('distanceFromRegion') ? 'Auto (km)' : ''}
                                className={fieldClass(canEditField('distanceFromRegion'), 'text-sm')}
                            />
                        </FormCell>
                        <FormCell className="flex flex-col justify-end lg:w-auto">
                            <span className="form-field-label opacity-0 select-none">Map</span>
                            <button
                                type="button"
                                title={gmapsKey?.trim() ? 'Pick on map' : 'Open in Google Maps'}
                                disabled={
                                    !(canEditField('latitude') || canEditField('longitude')) &&
                                    (!mapPinCoords.latitude || !mapPinCoords.longitude)
                                }
                                onClick={() => {
                                    if ((canEditField('latitude') || canEditField('longitude')) && gmapsKey?.trim()) setMapPickerOpen(true);
                                    else openMaps(mapPinCoords.latitude, mapPinCoords.longitude);
                                }}
                                className="inline-flex h-9 w-full max-w-[2.25rem] items-center justify-center rounded-lg border border-gray-300 bg-gray-100 text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-40 dark:border-gray-600 dark:bg-black/30 dark:text-gray-200 dark:hover:bg-black/50"
                            >
                                <Icon icon={FiMapPin} className="h-4 w-4" />
                            </button>
                        </FormCell>
                    </div>

                    {!isEditing && editedBranch && branch && (
                        <BranchRoutePreviewMap
                            apiKey={gmapsKey}
                            routeOrigin={mapRouteOrigin}
                            locationName={editedBranch.locationName || branch.locationName || ''}
                            locationId={String(editedBranch.locationId || branch.locationId || '')}
                            {...routePreviewCoords(branch, editedBranch)}
                        />
                    )}

                    {policeLookupLoading && isEditing && (
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                <Icon icon={FiLoader} className="h-3.5 w-3.5 animate-spin" />
                                Looking up…
                            </span>
                        </div>
                    )}

                    <FormRow cols={3}>
                        <FormCell>
                            <label className="form-field-label">Covered area</label>
                            {canEditField('coveredArea') ? (
                                <select
                                    value={editedBranch?.coveredArea || ''}
                                    onChange={(e) => handleFieldChange('coveredArea', e.target.value)}
                                    className="form-control"
                                >
                                    <option value="">—</option>
                                    {COVERED_AREA_OPTIONS.filter(Boolean).map((o) => (
                                        <option key={o} value={o}>
                                            {o}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    readOnly
                                    value={editedBranch?.coveredArea || '—'}
                                    className="form-control form-control-readonly"
                                />
                            )}
                        </FormCell>
                        <FormCell>
                            <TextField
                                label="Landmark"
                                value={editedBranch?.landmark || ''}
                                readOnly={!canEditField('landmark')}
                                onChange={(e) => handleFieldChange('landmark', e.target.value)}
                                placeholder="Area / landmark"
                                className={!canEditField('landmark') && isEditing ? '!cursor-not-allowed' : undefined}
                            />
                        </FormCell>
                        <FormCell>
                            <TextField
                                label="Nearest banks"
                                value={editedBranch?.nearestOtherBanksBranches || ''}
                                readOnly={!canEditField('nearestOtherBanksBranches')}
                                onChange={(e) => handleFieldChange('nearestOtherBanksBranches', e.target.value)}
                                placeholder="Meezan, UBL…"
                                className={!canEditField('nearestOtherBanksBranches') && isEditing ? '!cursor-not-allowed' : undefined}
                            />
                        </FormCell>
                    </FormRow>



                    <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-900/30">

                        <div className="w-full">
                            <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                <div>
                                    <label className="form-field-label">Police Station name</label>
                                    <input
                                        type="text"
                                        readOnly={!canEditField('nearestPoliceStationName')}
                                        value={editedBranch?.nearestPoliceStationName || ''}
                                        onChange={(e) => handleFieldChange('nearestPoliceStationName', e.target.value)}
                                        className={
                                            canEditField('nearestPoliceStationName')
                                                ? 'form-control max-w-full text-sm'
                                                : 'form-control form-control-readonly max-w-full text-sm'
                                        }
                                    />
                                </div>
                                <div>
                                    <label className="form-field-label">Police Station address</label>
                                    <input
                                        type="text"
                                        readOnly={!canEditField('nearestPoliceStationAddress')}
                                        value={editedBranch?.nearestPoliceStationAddress || ''}
                                        onChange={(e) => handleFieldChange('nearestPoliceStationAddress', e.target.value)}
                                        className={
                                            canEditField('nearestPoliceStationAddress')
                                                ? 'form-control max-w-full text-sm'
                                                : 'form-control form-control-readonly max-w-full text-sm'
                                        }
                                    />
                                </div>
                                <div>
                                    <label className="form-field-label">Police Station phone</label>
                                    <input
                                        type="text"
                                        readOnly={!canEditField('nearestPoliceStationPhone')}
                                        value={editedBranch?.nearestPoliceStationPhone || ''}
                                        onChange={(e) => handleFieldChange('nearestPoliceStationPhone', e.target.value)}
                                        className={
                                            canEditField('nearestPoliceStationPhone')
                                                ? 'form-control max-w-full text-sm'
                                                : 'form-control form-control-readonly max-w-full text-sm'
                                        }
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="w-full max-w-xl">
                            <span className="form-field-label">SRA level</span>
                            {canEditField('sraLevel') ? (
                                <div className="mt-1 flex flex-wrap gap-2">
                                    {SRA_LEVEL_OPTIONS.map((opt) => (
                                        <label
                                            key={opt.value}
                                            className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-2 py-1 text-xs transition dark:border-gray-600 ${
                                                editedBranch?.sraLevel === opt.value
                                                    ? 'border-gray-500 bg-white ring-1 ring-gray-400 dark:bg-gray-800'
                                                    : 'border-gray-200 bg-gray-50 dark:bg-gray-900/40'
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="sra-details"
                                                checked={editedBranch?.sraLevel === opt.value}
                                                onChange={() =>
                                                    setEditedBranch((prev: any) =>
                                                        prev
                                                            ? {
                                                                  ...prev,
                                                                  sraLevel: opt.value,
                                                                  sraReason:
                                                                      opt.value === 'orange' || opt.value === 'red'
                                                                          ? prev.sraReason
                                                                          : '',
                                                              }
                                                            : null
                                                    )
                                                }
                                                className="sr-only"
                                            />
                                            <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${opt.dotClass}`} />
                                            <span className="font-medium text-gray-800 dark:text-gray-200">{opt.label}</span>
                                            <span className="text-gray-500 dark:text-gray-400">({opt.hint})</span>
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <div className="mt-1">
                                    {(() => {
                                        const level = (editedBranch?.sraLevel || 'green') as BranchFormData['sraLevel'];
                                        const opt =
                                            SRA_LEVEL_OPTIONS.find((o) => o.value === level) ?? SRA_LEVEL_OPTIONS[0];
                                        return (
                                            <span className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800">
                                                <span className={`h-2.5 w-2.5 rounded-full ${opt.dotClass}`} />
                                                <span className="font-medium text-gray-800 dark:text-gray-200">
                                                    {opt.label}
                                                </span>
                                                <span className="text-gray-500 dark:text-gray-400">({opt.hint})</span>
                                            </span>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>

                        {(profileEdit
                            ? editedBranch?.sraLevel === 'orange' || editedBranch?.sraLevel === 'red'
                            : editedBranch?.sraLevel === 'orange' ||
                              editedBranch?.sraLevel === 'red' ||
                              !!(editedBranch?.sraReason && String(editedBranch.sraReason).trim())) && (
                            <div className="w-full max-w-md">
                                <label className="form-field-label">SRA reason</label>
                                <textarea
                                    readOnly={!canEditField('sraReason')}
                                    rows={2}
                                    value={editedBranch?.sraReason || ''}
                                    onChange={(e) => handleFieldChange('sraReason', e.target.value)}
                                    className={`resize-y py-2 text-sm max-w-full ${
                                        canEditField('sraReason') ? 'form-control' : 'form-control form-control-readonly'
                                    }`}
                                />
                            </div>
                        )}

                        <YesNoSwitch
                            label="Event-affected"
                            value={!!editedBranch?.eventAffectedEnabled}
                            onChange={(v) =>
                                setEditedBranch((prev: any) =>
                                    prev
                                        ? {
                                              ...prev,
                                              eventAffectedEnabled: v,
                                              ...(!v ? { eventAffectedAreasNote: '' } : {}),
                                          }
                                        : null
                                )
                            }
                            disabled={!canEditField('eventAffectedEnabled')}
                        />

                        {(profileEdit ? editedBranch?.eventAffectedEnabled : !!editedBranch?.eventAffectedEnabled) && (
                            <div className="w-full max-w-md">
                                <label className="form-field-label">Affected areas note</label>
                                <textarea
                                    readOnly={!canEditField('eventAffectedAreasNote')}
                                    rows={2}
                                    value={editedBranch?.eventAffectedAreasNote || ''}
                                    onChange={(e) => handleFieldChange('eventAffectedAreasNote', e.target.value)}
                                    className={`resize-y py-2 text-sm max-w-full ${
                                        canEditField('eventAffectedAreasNote') ? 'form-control' : 'form-control form-control-readonly'
                                    }`}
                                />
                            </div>
                        )}
                    </div>

                    <FormRow cols={4}>
                        <FormCell>
                            <label className="form-field-label">PSO assign</label>
                            {canEditField('psoId') ? (
                                <select
                                    value={editedBranch?.psoId || ''}
                                    onChange={(e) => handleFieldChange('psoId', e.target.value)}
                                    className="form-control"
                                >
                                    <option value="">Select</option>
                                    {psos.map((pso) => (
                                        <option key={pso.id} value={(pso as any).psoId || pso.id}>
                                            {pso.name}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    readOnly
                                    value={branch?.psoName || 'N/A'}
                                    className="form-control form-control-readonly"
                                />
                            )}
                        </FormCell>
                    </FormRow>

                    {/* Manager Information */}
                    {managerFields.map(({ key, label }) => {
                        const currentMgrId = editedBranch?.[key] ?? branch?.[key] ?? '';
                        // Look for manager in both managers prop and allManagers state
                        const currentMgr = currentMgrId
                            ? (managers[currentMgrId] || allManagers.find(m => m.id === currentMgrId) || null)
                            : null;
                        const editedMgr = editedManagers[key] || currentMgr;

                        return (
                            <div
                                key={key}
                                className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/30"
                            >
                                <h6 className="mb-4 text-sm font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    {label}
                                </h6>
                                <div className="space-y-4">
                                    {canEditField(key) ? (
                                        <SearchableSelect
                                            options={allManagers.map(mgr => ({
                                                id: mgr.id,
                                                label: mgr.name,
                                                value: mgr.id,
                                                details: {
                                                    email: mgr.email,
                                                    mobile: mgr.mobile,
                                                },
                                            }))}
                                            value={currentMgrId || ''}
                                            onChange={(value) => {
                                                handleFieldChange(key, value);
                                                const selectedMgr = allManagers.find(m => m.id === value);
                                                if (selectedMgr) {
                                                    setEditedManagers(prev => ({
                                                        ...prev,
                                                        [key]: { ...selectedMgr }
                                                    }));
                                                } else {
                                                    setEditedManagers(prev => ({
                                                        ...prev,
                                                        [key]: {}
                                                    }));
                                                }
                                            }}
                                            placeholder={`Select ${label} (Optional)`}
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            readOnly
                                            value={currentMgr ? currentMgr.name : 'No manager assigned'}
                                            className="form-control form-control-readonly"
                                        />
                                    )}
                                    {currentMgr && (
                                        <div className="space-y-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                                            <div>
                                                <label className="mb-1.5 block text-sm font-normal text-gray-500 dark:text-gray-400">
                                                    Name
                                                </label>
                                                <input
                                                    type="text"
                                                    readOnly={!canEditField(key)}
                                                    value={editedMgr?.name || currentMgr.name || ''}
                                                    onChange={(e) => setEditedManagers(prev => ({
                                                        ...prev,
                                                        [key]: { ...prev[key], name: e.target.value }
                                                    }))}
                                                    className="form-control"
                                                />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-sm font-normal text-gray-500 dark:text-gray-400">
                                                    Email
                                                </label>
                                                <input
                                                    type="email"
                                                    readOnly={!canEditField(key)}
                                                    value={editedMgr?.email || currentMgr.email || ''}
                                                    onChange={(e) => setEditedManagers(prev => ({
                                                        ...prev,
                                                        [key]: { ...prev[key], email: e.target.value }
                                                    }))}
                                                    className="form-control"
                                                />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-sm font-normal text-gray-500 dark:text-gray-400">
                                                    Mobile
                                                </label>
                                                <input
                                                    type="text"
                                                    readOnly={!canEditField(key)}
                                                    value={editedMgr?.mobile || currentMgr.mobile || ''}
                                                    onChange={(e) => setEditedManagers(prev => ({
                                                        ...prev,
                                                        [key]: { ...prev[key], mobile: e.target.value }
                                                    }))}
                                                    className="form-control"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    
                    {/* Deployments Section */}
                    <div className="mt-8 border-t border-gray-200 pt-6 dark:border-gray-800">
                        <h4 className="mb-4 text-base font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                            <Icon icon={FiLayers} className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                            Deployed Systems & Services
                        </h4>

                        {loadingDeployments ? (
                            <div className="flex items-center justify-center py-10">
                                <div className="flex flex-col items-center gap-2">
                                    <Icon icon={FiLoader} className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" />
                                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                        Loading deployment details...
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                                {/* CCTV Section */}
                                <div className="rounded-2xl border border-gray-200/80 bg-white p-5 shadow-sm transition hover:shadow-md dark:border-gray-700/60 dark:bg-black/15">
                                    <div className="mb-3 flex items-center justify-between">
                                        <h5 className="text-sm font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                            CCTV Surveillance
                                        </h5>
                                        <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-bold text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                                            CCTV
                                        </span>
                                    </div>
                                    <div className="space-y-2">
                                        {deploymentsData.cctv.length === 0 ? (
                                            <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                                                No CCTV vendor deployed
                                            </p>
                                        ) : (
                                            deploymentsData.cctv.map((item) => (
                                                <div
                                                    key={item.id}
                                                    className="flex items-center justify-between rounded-xl bg-gray-50 p-3 dark:bg-gray-800/40"
                                                >
                                                    <div>
                                                        <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                            {item.companyName}
                                                        </span>
                                                        <span className="text-[10px] text-gray-400">
                                                            Active Deployment
                                                        </span>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                {/* BAMS Section */}
                                <div className="rounded-2xl border border-gray-200/80 bg-white p-5 shadow-sm transition hover:shadow-md dark:border-gray-700/60 dark:bg-black/15">
                                    <div className="mb-3 flex items-center justify-between">
                                        <h5 className="text-sm font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                            BAMS Alarms
                                        </h5>
                                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-600 dark:bg-amber-900/30 dark:text-amber-400">
                                            BAMS
                                        </span>
                                    </div>
                                    <div className="space-y-2">
                                        {deploymentsData.bams.length === 0 ? (
                                            <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                                                No BAMS vendor deployed
                                            </p>
                                        ) : (
                                            deploymentsData.bams.map((item) => (
                                                <div
                                                    key={item.id}
                                                    className="flex items-center justify-between rounded-xl bg-gray-50 p-3 dark:bg-gray-800/40"
                                                >
                                                    <div>
                                                        <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                            {item.companyName}
                                                        </span>
                                                        <span className="text-[10px] text-gray-400">
                                                            Active Deployment
                                                        </span>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                {/* Guard Section */}
                                <div className="rounded-2xl border border-gray-200/80 bg-white p-5 shadow-sm transition hover:shadow-md dark:border-gray-700/60 dark:bg-black/15">
                                    <div className="mb-3 flex items-center justify-between">
                                        <h5 className="text-sm font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                            Physical Guarding
                                        </h5>
                                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
                                            Guard
                                        </span>
                                    </div>
                                    <div className="space-y-2">
                                        {deploymentsData.guard.length === 0 ? (
                                            <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                                                No guard vendor deployed
                                            </p>
                                        ) : (
                                            deploymentsData.guard.map((item) => (
                                                <div
                                                    key={item.id}
                                                    className="flex items-center justify-between rounded-xl bg-gray-50 p-3 dark:bg-gray-800/40"
                                                >
                                                    <div>
                                                        <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                            {item.companyName}
                                                        </span>
                                                        <span className="text-[10px] text-gray-400">
                                                            Active Deployment
                                                        </span>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    {isEditing ? (
                        <>
                            <Button variant="secondary" onClick={handleCancel} disabled={loading}>
                                Cancel
                            </Button>
                            <Button onClick={handleSave} disabled={loading}>
                                {loading ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </>
                    ) : (
                        <Button variant="secondary" onClick={handleClose}>
                            Close
                        </Button>
                    )}
                </div>
            </div>
        </Modal>
        <BranchMapPickerModal
            isOpen={mapPickerOpen}
            onClose={() => setMapPickerOpen(false)}
            apiKey={gmapsKey}
            routeOrigin={mapRouteOrigin}
            latitude={editedBranch?.latitude}
            longitude={editedBranch?.longitude}
            onSubmit={(lat, lng) => {
                setEditedBranch((prev: any) =>
                    prev ? { ...prev, latitude: String(lat), longitude: String(lng) } : null
                );
                void fillNearbyFromCoords(lat, lng);
            }}
        />
        {isAddDeptModalOpen && (
            <Modal
                isOpen={isAddDeptModalOpen}
                onClose={() => {
                    setIsAddDeptModalOpen(false);
                    setNewDeptName('');
                }}
                title="Create New Department"
                size="sm"
                closeOnBackdropClick={false}
            >
                <div className="p-4 sm:p-6 flex flex-col space-y-4">
                    <div>
                        <label className="form-field-label">Department Name</label>
                        <input
                            type="text"
                            value={newDeptName}
                            onChange={(e) => setNewDeptName(e.target.value)}
                            placeholder="e.g. Prestige"
                            required
                            className="form-control mt-1 w-full"
                        />
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => {
                                setIsAddDeptModalOpen(false);
                                setNewDeptName('');
                            }}
                            disabled={creatingDept}
                            className="!py-2"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleCreateDepartment}
                            disabled={creatingDept || !newDeptName.trim()}
                            className="!py-2"
                        >
                            {creatingDept ? 'Creating...' : 'Create & Add'}
                        </Button>
                    </div>
                </div>
            </Modal>
        )}
        </>
    );
}
